import { Alert, Box, Button, useBreakpoint } from '@palmetto/palmetto-components';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { ProgramType, ToleranceException, RequirementFlagSettingsType } from 'types';

import { formatKWHNumber } from '../../helpers/kwhFormatter';
import { percentageFormatter } from '../../helpers/percentageFormatter';
import { checkValueChangeTolerance } from '../../helpers/productionValueCheck';
import { Origin } from '../../types/ProductionValueCheck';
import { ReviewPackageActions } from '../../types/ReviewPackage';
import { useAuth } from '../auth/authProvider';
import RequirePalmettoFinanceUser, { isPalmettoFinanceUser } from '../auth/RequirePalmettoFinanceUser';
import FormSection from '../Forms/FormSection';
import { ChangeOrderDocumentSection } from '../MilestonePackagePartials/ChangeOrderDocumentSection';
import { ContractDocumentSection } from '../MilestonePackagePartials/ContractDocumentSection';
import { CommonFormSection } from '../ProductionValueCheck/Form';
import { FlagBox } from '../QueueFlags/FlagBox';
import { QueueFlagsContext } from '../QueueFlags/QueueFlagsContext';

import { ContractKwhInfoDescription } from './InstallationFormSectionDescriptions';

type ContractKwhInfoProps = {
  firstYearContracted: number;
  firstYearInstalled: number;
  toleranceException?: ToleranceException;
  outOfRange: boolean;
  hideFlags?: boolean;
  programType?: ProgramType;
};
export const ContractKwhInfo = ({
  firstYearContracted,
  firstYearInstalled,
  toleranceException,
  outOfRange,
  hideFlags = false,
  programType = ProgramType.solar,
}: ContractKwhInfoProps) => {
  const { flagDrivenPackageReview = false } = useFlags();
  const navigate = useNavigate();
  const { id: accountId, action } = useParams<{ id: any; action: ReviewPackageActions }>();
  const { getFlagsByFlagType } = useContext(QueueFlagsContext);
  const { isPhone, isTablet } = useBreakpoint();
  const isMobile = isPhone || isTablet;
  const { claims } = useAuth();
  const isFinCoUser = isPalmettoFinanceUser(claims);
  const customerContractFlags = getFlagsByFlagType(RequirementFlagSettingsType.customerContract);
  let percentDiff: number = 0;

  if (firstYearContracted !== 0) {
    const diffResults = checkValueChangeTolerance({
      newValue: firstYearInstalled,
      originalValue: firstYearContracted,
    });
    percentDiff =
      Number(formatKWHNumber(firstYearInstalled)) < Number(formatKWHNumber(firstYearContracted))
        ? diffResults.percentDifference * -1
        : diffResults.percentDifference;
  } else {
    percentDiff = 1;
  }

  const handleOnClick = () => {
    navigate(`/accounts/${accountId}/production-value-check`, { state: { from: Origin.INSTALL_PACKAGE, action } });
  };

  return (
    <>
      <CommonFormSection title="Contracted Year-1 Total Production Estimate">
        <Box fontWeight={'medium'} fontSize={'lg'}>
          {formatKWHNumber(firstYearContracted)} kWh
        </Box>
      </CommonFormSection>
      <CommonFormSection title="Installed Year-1 Total Production Estimate">
        <Box fontWeight={'medium'} fontSize={'lg'} display="flex" direction="row" height="" gap="xs">
          {formatKWHNumber(firstYearInstalled)} kWh{' '}
          <Button variant="primary" tone="neutral" size="xs" onClick={handleOnClick}>
            edit
          </Button>
        </Box>
      </CommonFormSection>
      <FormSection title="Difference" description={ContractKwhInfoDescription(programType)} className="border-out">
        <Box
          fontWeight={'medium'}
          fontSize={'lg'}
          display={{ base: 'flex' }}
          direction={{ base: 'column', tablet: 'row' }}
        >
          <Box margin="xl 0 0 0" color={outOfRange && !toleranceException ? 'danger' : 'body-primary'}>
            {percentageFormatter.format(percentDiff)}
          </Box>
          <Box margin="0 0 0 xl">
            {toleranceException && (
              <Alert
                variant="warning"
                hasIcon
                title="Production Tolerance Exception"
                message={
                  <RequirePalmettoFinanceUser>
                    {toleranceException.note} - {toleranceException.exceptionGivenBy}
                  </RequirePalmettoFinanceUser>
                }
              />
            )}
          </Box>
        </Box>
      </FormSection>
      {flagDrivenPackageReview && (isFinCoUser || customerContractFlags) ? (
        <ContractDocumentSection showSeparatorBorder={false} />
      ) : null}
      {isFinCoUser ? <ChangeOrderDocumentSection showSeparatorBorder={false} /> : null}
      {!hideFlags && (
        <Box style={{ marginLeft: isMobile ? '' : '18rem' }}>
          <FlagBox
            dataKey="Year-1 Production Estimate"
            flagType={RequirementFlagSettingsType.yearOneProductionEstimate}
          />
        </Box>
      )}
    </>
  );
};
